// libs
import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

// helpers
import {
  Impression,
  IMPRESSION_TYPE,
  syncImpressionsHandler,
} from 'shared/helpers/utils/impressions';
import { getCustomerId } from 'shared/store/app/selectors';
import { getRemoteConfigs } from 'shared/store/remoteConfigs/selectors';
import { asyncImpressionsHandler } from './helpers';
import useWishlistHandler from './useWishlistHandler';

// components
import PersonalizedWidgetRenderer from '@nykaa/deals-personalization';

// styles
import { themeDealsPlatform } from 'shared/styles/base';

// defs
import { ILandingPageProps } from 'shared/components/AdPlatformWidgets/types';

// constants
import { EAGER_IMAGE_LOADING_COUNT } from 'shared/constants';

// helpers
import { getDomainConfig } from 'shared/domainConfig/helpers';

interface ILandingPageChildProps {
  landingPageData: ILandingPageProps['landingPageData'];
  eagerLoadingCount?: number;
  isPersonalizationSupported?: boolean;
  pageData?: string;
  contextPayload?: Record<string, unknown>;
  store?: string;
  apiHelper?: (apiURL: string, method: string, bodyData?: unknown) => Promise<{ data: unknown }>;
  dealsEndpoint?: string;
  handleAddToCart?: (
    params: Record<string, any>,
    setIsLoading: (val: boolean) => void,
    trackingData: Record<string, unknown>
  ) => void;
}

const Child = ({
  landingPageData,
  eagerLoadingCount = EAGER_IMAGE_LOADING_COUNT,
  isPersonalizationSupported = false,
  pageData,
  contextPayload,
  store: storeProp,
  apiHelper,
  dealsEndpoint: dealsEndpointProp,
  handleAddToCart,
}: ILandingPageChildProps) => {
  const customerId = useSelector(getCustomerId);
  const remoteConfigs = useSelector(getRemoteConfigs);

  const asyncImpressionCallback = async (impressions: Impression[] = []) =>
    asyncImpressionsHandler(impressions, customerId);

  const syncImpressionCallback = (impressions: Impression[] = [], type: IMPRESSION_TYPE) =>
    syncImpressionsHandler(impressions, type);

  const { wishlistData, handleWishList } = useWishlistHandler();

  const { DN_STORE } = getDomainConfig();
  const personalizationEndpoint = dealsEndpointProp || '/dp';
  const personalizationProps = isPersonalizationSupported
    ? {
        isPersonalizationSupported: true as const,
        ApiHelper: apiHelper,
        dealsEndpoint: personalizationEndpoint,
        ...(pageData !== undefined && { pageData }),
        ...(contextPayload !== undefined && { contextPayload }),
      }
    : {
        isPersonalizationSupported: false as const,
      };

  const memoizedRemoteConfigs = useMemo(
    () => ({
      configs: {
        relativePathDomains: remoteConfigs?.configs?.relativePathDomains || [],
        reactVersion: 18
      },
      switches: {
        optimizedImageUrlEnabled: remoteConfigs?.switches?.optimizedImageUrlEnabled ?? true,
      },
    }),
    [remoteConfigs]
  );

  return (
    <>
      <PersonalizedWidgetRenderer
        widgetList={landingPageData}
        isServer={__SERVER__}
        isDesktop={false}
        {...personalizationProps}
        asyncImpressionCallback={asyncImpressionCallback}
        syncImpressionCallback={syncImpressionCallback}
        eagerLoadImages={eagerLoadingCount}
        eagerLoadInventories={eagerLoadingCount}
        theme={themeDealsPlatform}
        store={storeProp || DN_STORE}
        remoteConfigs={memoizedRemoteConfigs}
        cta={{
          wishlist: {
            handleWishList,
            wishlistData,
          },
          ...(handleAddToCart && { handleAddToCart }),
        }}
      />
    </>
  );
};

export default memo(Child);
